import React, { useContext, useEffect, useState } from 'react';
import { Box, Button, Skeleton, SxProps, Typography, useTheme } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { Accordion, useModal } from '@europrocurement/flexy-components';
import {
    DOC_TYPE_DOWNLOADABLE,
    DOC_TYPE_INTERNAL,
    DOC_TYPE_REQUIRED,
    type DocTypeEnumType,
    findNameByDocTypeId,
} from '@europrocurement/l2d-domain';
import useDossierFile from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useDossierFile';
import { type DisplayedDocument } from '@europrocurement/l2d-modules/modules/Dossiers/providers/types';
import {
    DocumentContext,
    useDocumentPreviewContext,
    useSelectedDossierContext,
} from '@europrocurement/l2d-modules/modules/Dossiers/providers';
import useDossierDocuments, {
    type DocumentExtension,
    type DossierFichier,
} from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useDossierDocuments';
import useDossierDocumentsFiles from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useDossierDocumentsFiles';
import { LAYOUT_DOCUMENTS_FULL } from '@europrocurement/l2d-modules/modules/Dossiers/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import RequiredDocumentsList from '../../RequiredDocumentsList/RequiredDocumentsList';
import RequiredDocumentUploader from '../../RequiredDocumentUploader/RequiredDocumentUploader';
import { type DocumentTypeItem, DocumentTypeForm } from '../../../shared';

export type DossierDocumentsValidationListProps = {
    displayList: boolean;
    currentLayout?: string;
};

const DossierDocumentsValidationList: React.FunctionComponent<DossierDocumentsValidationListProps> =
    function (props) {
        const { displayList, currentLayout } = props;
        const { dossier, siblings } = useSelectedDossierContext();
        const [displayedDocType, setDisplayedDocType] =
            useState<DocTypeEnumType>(DOC_TYPE_REQUIRED);
        const [listItems, setListItems] = useState<DisplayedDocument[]>([]);
        const { getFilePreview } = useDossierDocumentsFiles();
        const { downloadFichier, isFileSupported, postDossierTypeDocuments } =
            useDossierDocuments();
        const {
            fichiers,
            documents,
            documentDataLoaded,
            getInternalDocuments,
            getInternalAdditionalDocuments,
            getPrescriberDocuments,
            getPrescriberAdditionalDocuments,
            getFormalisteDocuments,
            getFormalisteAdditionalDocuments,
            refreshDocumentContext,
        } = useContext(DocumentContext);
        const dataLoaded = documentDataLoaded();
        const { setPreviewLoading, documentPreview, setDocumentPreview, setListDocuments } =
            useDocumentPreviewContext();
        const { palette } = useTheme();
        const [uploadingAdditionalDocument, setUploadingAdditionalDocument] =
            useState<boolean>(false);
        const {
            handleAddFile,
            handleRemoveFile,
            handleAdditionalUploads,
            handleMoveFile,
            handleRemoveDocument,
        } = useDossierFile({
            listItems,
            setListItems,
            typeDoc: displayedDocType as
                | typeof DOC_TYPE_REQUIRED
                | typeof DOC_TYPE_INTERNAL
                | typeof DOC_TYPE_DOWNLOADABLE,
            setUploadingAdditionalDocument,
            source: 'BACK',
        });
        const { modalActions } = useModal();

        // Get displayed doc type list based on query params
        useEffect(() => {
            const urlParams = new URLSearchParams(document.location.search);
            const docType = urlParams.get('docType');
            if (docType) {
                setDisplayedDocType(+docType as DocTypeEnumType);
            }
        }, []);

        const handleSelect = async (
            documentLabel: string,
            fichierId: number,
            documentId: number,
        ) => {
            setPreviewLoading(true);

            try {
                const documentToShow = await getFilePreview(documentId, fichierId);

                if (!isFileSupported(documentToShow.fileType as DocumentExtension)) {
                    const fichier = fichiers.find(
                        (f: DossierFichier) => Number(f.id) === fichierId,
                    );

                    if (fichier) {
                        await downloadFichier(fichier);
                    }
                }

                setDocumentPreview(documentToShow);
                setPreviewLoading(false);
            } catch (err) {
                console.log(err);
                enqueueSnackbar(
                    `Une erreur est survenue lors de l'affichage du document ${documentLabel}`,
                    { variant: 'error' },
                );
            }
        };

        // Display modal of document types to add
        const handleOpenAddDocumentModal = () => {
            modalActions.call(
                <DocumentTypeForm
                    title="Ajout de type de document"
                    itemsSelected={[]}
                    type={displayedDocType}
                    handleSelectItems={(documentItems: DocumentTypeItem[]) => {
                        if (documentItems && documentItems.length > 0) {
                            documentItems.map(async (docItem) => {
                                const newDosDoc = await postDossierTypeDocuments(
                                    dossier.id as number,
                                    docItem.id,
                                    docItem.label,
                                    dossier.statutId as number,
                                );
                                if (newDosDoc) {
                                    refreshDocumentContext();
                                    modalActions.close();
                                }
                            });
                        } else {
                            enqueueSnackbar(`Type de document manquant`, {
                                variant: 'error',
                            });
                        }
                    }}
                />,
            );
        };
        // Load list of documents by docType
        useEffect(() => {
            if (dataLoaded) {
                let arrayOfDatas: DisplayedDocument[] = [];

                if (displayedDocType === DOC_TYPE_REQUIRED) {
                    arrayOfDatas = [
                        ...getPrescriberDocuments(),
                        ...getPrescriberAdditionalDocuments(),
                    ];
                } else if (displayedDocType === DOC_TYPE_INTERNAL) {
                    arrayOfDatas = [...getInternalDocuments(), ...getInternalAdditionalDocuments()];
                } else if (displayedDocType === DOC_TYPE_DOWNLOADABLE) {
                    arrayOfDatas = [
                        ...getFormalisteDocuments(),
                        ...getFormalisteAdditionalDocuments(),
                    ];
                }

                // DO not display actions on this list
                arrayOfDatas = arrayOfDatas.map((data) => ({
                    ...data,
                    downloadable: false,
                    showable: !!data.fichierId,
                    uploadable: !data.fichierId,
                    smallUploadableBtn: true,
                }));

                setListItems(arrayOfDatas);
                setListDocuments(arrayOfDatas);

                // display the first document with file as preview
                const document = arrayOfDatas.find((doc) => doc.fichierId);
                if (document && !documentPreview) {
                    handleSelect(document.label, document.fichierId as number, document.id);
                }
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [displayedDocType, dataLoaded, fichiers, documents]);

        let wrapperSx: SxProps = {
            display: displayList ? 'flex' : 'none',
            flexDirection: 'column',
            gap: '8px 0',
            backgroundColor: palette.background.paper,
            borderRightColor: palette.grey[200],
            borderRightWidth: '1px',
            borderRightStyle: 'solid',
            padding: '8px 20px',
            overflowY: 'scroll',
            position: 'absolute',
            zIndex: 100,
            top: '0',
            left: '0',
            width: '40%',
            height: '100%',
        };

        if (currentLayout && currentLayout === LAYOUT_DOCUMENTS_FULL) {
            wrapperSx = { ...wrapperSx, width: '30%', position: 'sticky', overflowY: 'auto' };
        } else {
            wrapperSx = { ...wrapperSx, width: '45%', position: 'sticky', overflowY: 'auto' };
        }
        return (
            <Box sx={wrapperSx}>
                {!dataLoaded && (
                    <Box
                        sx={{
                            marginTop: '15px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.75em 0',
                        }}
                    >
                        <Skeleton
                            variant="rectangular"
                            role="progressbar"
                            sx={{
                                bgcolor: 'grey.200',
                                borderRadius: '12px',
                                width: '100%',
                                height: '80px',
                            }}
                        />
                        <Skeleton
                            variant="rectangular"
                            role="progressbar"
                            sx={{
                                bgcolor: 'grey.200',
                                borderRadius: '12px',
                                width: '100%',
                                height: '80px',
                            }}
                        />
                        <Skeleton
                            variant="rectangular"
                            role="progressbar"
                            sx={{
                                bgcolor: 'grey.200',
                                borderRadius: '12px',
                                width: '100%',
                                height: '80px',
                            }}
                        />
                    </Box>
                )}
                {dataLoaded && (
                    <>
                        {siblings.map((doss) => {
                            const docs = listItems.filter((item) => item.dossierId === doss.id);

                            if (!documents.length) {
                                return null;
                            }

                            const title = (
                                <Typography
                                    variant="l2dbodym"
                                    sx={{ color: palette.primary.dark }}
                                >
                                    {doss.numero}
                                </Typography>
                            );

                            return (
                                <Accordion
                                    key={`accordion-${doss.numero}`}
                                    title={title}
                                    expandLabel={null}
                                    expandColor={palette.primary}
                                    expanded
                                >
                                    <RequiredDocumentsList
                                        documents={docs}
                                        onUpload={handleAddFile}
                                        onDeletion={handleRemoveFile}
                                        onDocDeletion={handleRemoveDocument}
                                        onDownload={() => {}}
                                        onShow={handleSelect}
                                        onMove={handleMoveFile}
                                        onClick={() => {}}
                                    />
                                </Accordion>
                            );
                        })}
                        {displayedDocType && (
                            <Button
                                onClick={() => handleOpenAddDocumentModal()}
                                sx={{
                                    fontWeight: 500,
                                    padding: '8px 20px',
                                    color: palette.primary.dark,
                                }}
                            >
                                <FontAwesomeIcon icon={faPlus} />

                                <Typography variant="l2dbodys">
                                    Ajouter un document {findNameByDocTypeId(displayedDocType)}
                                </Typography>
                            </Button>
                        )}
                        <RequiredDocumentUploader
                            uploading={uploadingAdditionalDocument}
                            onChange={handleAdditionalUploads}
                            showUploadBtn={false}
                        />
                    </>
                )}
            </Box>
        );
    };

export default DossierDocumentsValidationList;
